import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { pad, parseISO, fmtISO, addDays, addMonths, weekStart, monthLabel, DOW_HEADERS } from './plannerUtils';

/* A compact month calendar for the Planner sidebar.
   - mode 'day'  : highlights the anchored day; clicking a day opens it.
   - mode 'week' : highlights the whole week row of the anchor; clicking any
                   day opens that week.
   The displayed month follows the anchor but can be browsed independently. */
export default function PlannerMiniCalendar({ anchor, today, mode = 'day', onPick }) {
  const [cursor, setCursor] = useState(() => (anchor || today || '').slice(0, 7)); // 'YYYY-MM'
  useEffect(() => { if (anchor) setCursor(anchor.slice(0, 7)); }, [anchor]);

  const { weeks, cy, cm } = useMemo(() => {
    const [y, m] = cursor.split('-').map(Number);
    const firstOfMonth = `${cursor}-01`;
    const lastDayNum = new Date(y, m, 0).getDate();
    const gridStart = weekStart(firstOfMonth);
    const gridEnd = addDays(weekStart(`${cursor}-${pad(lastDayNum)}`), 6);
    const days = [];
    let d = gridStart;
    while (d <= gridEnd) { days.push(d); d = addDays(d, 1); }
    const rows = [];
    for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7));
    return { weeks: rows, cy: y, cm: m };
  }, [cursor]);

  const selWeek = mode === 'week' && anchor ? weekStart(anchor) : null;

  return (
    <div className="rounded-[var(--p-radius-lg)] border border-[color:var(--p-border)] bg-[color:var(--p-card)] p-3 shadow-[var(--p-shadow)]" data-testid="planner-mini-calendar">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[12.5px] font-semibold text-[color:var(--p-text)]" data-testid="planner-mini-calendar-month">{monthLabel(`${cursor}-01`)}</div>
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => setCursor(addMonths(`${cursor}-01`, -1).slice(0, 7))} aria-label="Previous month"
            data-testid="planner-mini-calendar-prev"
            className="n-focus grid h-6 w-6 place-items-center rounded-md text-[color:var(--p-muted)] transition-colors hover:bg-[color:var(--p-accent-bg)] hover:text-[color:var(--p-accent)]"><ChevronLeft size={15} /></button>
          <button type="button" onClick={() => setCursor(addMonths(`${cursor}-01`, 1).slice(0, 7))} aria-label="Next month"
            data-testid="planner-mini-calendar-next"
            className="n-focus grid h-6 w-6 place-items-center rounded-md text-[color:var(--p-muted)] transition-colors hover:bg-[color:var(--p-accent-bg)] hover:text-[color:var(--p-accent)]"><ChevronRight size={15} /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {DOW_HEADERS.map((d) => (
          <div key={d} className="pb-1 text-center text-[9.5px] font-semibold uppercase tracking-wide text-[color:var(--p-muted-2)]">{d.charAt(0)}</div>
        ))}
      </div>
      <div className="space-y-0.5">
        {weeks.map((row) => {
          const rowSelected = selWeek && weekStart(row[0]) === selWeek;
          return (
            <div key={row[0]} className={`grid grid-cols-7 gap-0.5 rounded-md ${rowSelected ? 'bg-[color:var(--p-accent-bg)]' : ''}`} data-testid={rowSelected ? 'planner-mini-calendar-week-selected' : 'planner-mini-calendar-week'}>
              {row.map((ds) => {
                const dd = parseISO(ds);
                const inMonth = dd.getFullYear() === cy && (dd.getMonth() + 1) === cm;
                const isToday = ds === today;
                const isSelDay = mode === 'day' && ds === anchor;
                return (
                  <button
                    key={ds}
                    type="button"
                    onClick={() => onPick && onPick(ds)}
                    data-testid={isSelDay ? 'planner-mini-calendar-day-selected' : 'planner-mini-calendar-day'}
                    aria-label={ds}
                    aria-pressed={isSelDay || rowSelected}
                    className={`n-focus relative grid h-7 place-items-center rounded-md text-[11.5px] font-medium transition-colors
                      ${isSelDay
                        ? 'bg-[color:var(--p-accent)] text-white'
                        : rowSelected
                          ? 'text-[color:var(--p-text)] hover:bg-[color:var(--p-accent)]/15'
                          : 'hover:bg-[color:var(--p-accent-bg)]'}
                      ${!isSelDay && !rowSelected && inMonth ? 'text-[color:var(--p-text)]' : ''}
                      ${!inMonth && !isSelDay ? 'text-[color:var(--p-muted-2)] opacity-60' : ''}`}
                  >
                    <span className="p-tnum">{dd.getDate()}</span>
                    {isToday && !isSelDay && <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-[color:var(--p-accent)]" />}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
